import React, { useState } from 'react';
import { useAuth, API } from '@/App';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Lock, Eye, EyeOff, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

export default function ForceChangePassword() {
  const { user, logout } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPassword.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    if (newPassword !== confirmPassword) { toast.error('Passwords do not match'); return; }
    if (currentPassword === newPassword) { toast.error('New password must be different'); return; }

    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/force-change-password`, {
        current_password: currentPassword,
        new_password: newPassword,
      });
      toast.success('Password set successfully');
      setDone(true);
      setTimeout(() => { window.location.href = '/dashboard'; }, 1500);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-[#080C14] flex items-center justify-center p-4">
        <Card className="bg-[#0D1320] border-white/[0.06] w-full max-w-md">
          <CardContent className="p-8 text-center">
            <CheckCircle className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2">Password Set</h2>
            <p className="text-white/50 text-sm">Taking you to your dashboard...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#080C14] flex items-center justify-center p-4">
      <div className="w-full max-w-md space-y-6">
        <div className="text-center">
          <span className="text-[#C89B7B] text-2xl font-semibold tracking-wide">ReGenfl&#x14D;</span>
          <p className="text-white/20 text-[10px] tracking-[3px] uppercase mt-1">Care OS</p>
        </div>

        <Card className="bg-[#0D1320] border-white/[0.06]">
          <CardContent className="p-8">
            <div className="text-center mb-6">
              <Lock className="w-8 h-8 text-[#C89B7B] mx-auto mb-3" />
              <h2 className="text-lg font-semibold text-white" data-testid="force-change-title">Set Your Password</h2>
              <p className="text-white/40 text-sm mt-1">
                Please set your own password to continue.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-xs text-white/50 mb-1 block">Current Temporary Password</label>
                <div className="relative">
                  <Input data-testid="current-password-input"
                    type={showCurrent ? 'text' : 'password'}
                    value={currentPassword}
                    onChange={e => setCurrentPassword(e.target.value)}
                    className="bg-white/[0.03] border-white/[0.08] text-white pr-10"
                    placeholder="Enter temporary password"
                    required
                  />
                  <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30"
                    onClick={() => setShowCurrent(!showCurrent)}>
                    {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="text-xs text-white/50 mb-1 block">New Password</label>
                <div className="relative">
                  <Input data-testid="new-password-input"
                    type={showNew ? 'text' : 'password'}
                    value={newPassword}
                    onChange={e => setNewPassword(e.target.value)}
                    className="bg-white/[0.03] border-white/[0.08] text-white pr-10"
                    placeholder="At least 8 characters"
                    required
                    minLength={8}
                  />
                  <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30"
                    onClick={() => setShowNew(!showNew)}>
                    {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {newPassword.length > 0 && newPassword.length < 8 && (
                  <p className="text-[10px] text-red-400/60 mt-1">Minimum 8 characters</p>
                )}
              </div>

              <div>
                <label className="text-xs text-white/50 mb-1 block">Confirm New Password</label>
                <Input data-testid="confirm-password-input"
                  type="password"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  className="bg-white/[0.03] border-white/[0.08] text-white"
                  placeholder="Repeat new password"
                  required
                />
                {confirmPassword && confirmPassword !== newPassword && (
                  <p className="text-[10px] text-red-400/60 mt-1">Passwords do not match</p>
                )}
              </div>

              <Button data-testid="submit-password-change" type="submit" disabled={submitting}
                className="w-full bg-[#C89B7B] hover:bg-[#B88A6A] text-[#080C14] font-semibold">
                {submitting ? 'Setting...' : 'Set Password & Continue'}
              </Button>
            </form>
          </CardContent>
        </Card>

        <p className="text-center text-white/15 text-[10px]">
          Lifely ReGenfl&#x14D; Care OS
        </p>
      </div>
    </div>
  );
}
